import { Status, Wrapper } from '@googlemaps/react-wrapper';
import axios from 'axios';
import { observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { ReactElement } from 'react';
import { Card, CardContent, Grid, GridColumn, Notice, Select, Spinner, TSelectSimpleValue } from 'telia-front-react';
import Config from '../Config';
import { TranslationsContext } from '@teliaee/telia-translate';

interface SignalMapProps {
  hotSpots?: MapPointProps[];
  id?: string;
  style?: React.CSSProperties;
  url?: string;
  isNode?: boolean;
}

interface MapPointProps extends google.maps.LatLngLiteral {
  name: string;
  zoom: number;
}

const API_KEY = 'AIzaSyDgIGjt91QP5bteMlptDLRj-PX9p9No3jA';

@observer
class SignalMap extends React.Component<SignalMapProps> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;
  @observable accessor fetched: boolean = false;
  @observable accessor center: google.maps.LatLngLiteral = {
    lat: 58.595272,
    lng: 25.013607,
  };
  @observable accessor zoom: number = 7;
  private json: any = null;

  public async componentDidMount() {
    if (!this.props.isNode && !this.json) {
      this.json = (await axios.get(`${Config.teliaWebUrl}/maps/5g.json`)).data;
      this.fetched = true;
      if (this.props.hotSpots?.length) {
        this.center = {
          lat: this.props.hotSpots[0].lat,
          lng: this.props.hotSpots[0].lng,
        };
        this.zoom = this.props.hotSpots[0].zoom;
      }
    }
  }

  public isMapReady = (status: Status): ReactElement => {
    const translate = this.context.translate;
    if (status === Status.LOADING) {
      return <Spinner />;
    }
    if (status === Status.FAILURE) {
      return <Notice>{translate('general.error.message')}</Notice>;
    }
    return null;
  };

  private handleLocationChange = (newValue: TSelectSimpleValue) => {
    const selectedValue = this.props.hotSpots.find((v: MapPointProps) => v.name === newValue);
    if (selectedValue) {
      this.center = { lat: selectedValue.lat, lng: selectedValue.lng };
      this.zoom = selectedValue.zoom;
    }
  };

  public render() {
    const translate = this.context.translate;
    const { hotSpots, id, style, url } = this.props;

    return (
      <div style={{ position: 'relative', zIndex: 1 }}>
        <Wrapper apiKey={API_KEY} render={this.isMapReady} url={url}>
          {this.fetched ? (
            <>
              {hotSpots?.length && (
                <>
                  <Grid
                    align={['middle-xs', 'start-sm']}
                    style={{
                      zIndex: 3,
                      position: 'absolute',
                      bottom: '30px',
                      right: '20vw',
                      left: '30px',
                    }}
                  >
                    <GridColumn width={['xs-12', 'sm-6', 'lg-3']}>
                      <Card>
                        <CardContent background="neutral-bg-level-1">
                          <Select
                            id={'signalmap-select-location'}
                            label={translate('signalmap.region')}
                            defaultValue={hotSpots[0].name}
                            onChange={(v) => this.handleLocationChange(v)}
                            simpleValue={true}
                            options={hotSpots.map((v) => ({
                              value: v.name,
                              label: v.name,
                            }))}
                          />
                        </CardContent>
                      </Card>
                    </GridColumn>
                  </Grid>
                </>
              )}
              <Map center={this.center} zoom={this.zoom} geoJson={this.json} id={id} style={style} />
            </>
          ) : (
            <Spinner />
          )}
        </Wrapper>
      </div>
    );
  }
}

const Map = ({
  center,
  zoom,
  geoJson,
  id,
  style,
}: {
  center: google.maps.LatLngLiteral;
  zoom: number;
  geoJson?: any;
  id?: string;
  style?: React.CSSProperties;
}) => {
  const [map, setMap] = React.useState<google.maps.Map>();
  const ref = React.useRef();

  React.useEffect(() => {
    if (ref.current && !map) {
      const tempMap = new window.google.maps.Map(ref.current, {
        center,
        zoom,
        streetViewControl: false,
        mapTypeControl: true,
        mapTypeControlOptions: {
          mapTypeIds: [google.maps.MapTypeId.ROADMAP, google.maps.MapTypeId.SATELLITE],
        },
        styles: [
          {
            featureType: 'administrative',
            elementType: 'labels.text.fill',
            stylers: [
              {
                color: '#444444',
              },
            ],
          },
          {
            featureType: 'landscape',
            elementType: 'all',
            stylers: [
              {
                color: '#f2f2f2',
              },
            ],
          },
          {
            featureType: 'poi',
            elementType: 'all',
            stylers: [
              {
                visibility: 'off',
              },
            ],
          },
          {
            featureType: 'road',
            elementType: 'all',
            stylers: [
              {
                saturation: -100,
              },
              {
                lightness: 45,
              },
            ],
          },
          {
            featureType: 'road.highway',
            elementType: 'all',
            stylers: [
              {
                visibility: 'simplified',
              },
            ],
          },
          {
            featureType: 'road.arterial',
            elementType: 'labels.icon',
            stylers: [
              {
                visibility: 'off',
              },
            ],
          },
          {
            featureType: 'transit',
            elementType: 'all',
            stylers: [
              {
                visibility: 'off',
              },
            ],
          },
          {
            featureType: 'water',
            elementType: 'all',
            stylers: [
              {
                color: '#CCEAFF',
              },
              {
                visibility: 'on',
              },
            ],
          },
        ],
      });
      tempMap.data.setStyle({
        fillColor: '#990AE3',
        strokeWeight: 0,
        fillOpacity: 0.5,
      });
      tempMap.data.addGeoJson(geoJson);
      setMap(tempMap);
    } else {
      map.setOptions({ center, zoom });
    }
  });

  return (
    <div
      style={{
        zIndex: 2,
        position: 'inherit',
        height: '75vh',
        width: '100%',
        ...style,
      }}
      id={id || '5g-map'}
      ref={ref}
    />
  );
};

export default inject('isNode')(SignalMap);
